/**
 * LinkedIn Metrics Service
 *
 * CRUD for daily LinkedIn stats stored in linkedin_metrics/{dateKey}.
 * Follows LeadManagementService.ts pattern.
 */

import { db } from '../firebaseConfig';
import {
  collection,
  query,
  getDocs,
  getDoc,
  doc,
  setDoc,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import type { LinkedInDailyMetrics } from '../types/outreach';

const METRICS_COLLECTION = 'linkedin_metrics';

function getMetricsCollection() {
  return collection(db, METRICS_COLLECTION);
}

function todayDateKey(): string {
  return new Date().toISOString().split('T')[0];
}

export class LinkedInMetricsService {
  /**
   * Get metrics for a specific date.
   */
  static async getMetrics(dateKey: string): Promise<LinkedInDailyMetrics | null> {
    try {
      const docRef = doc(db, METRICS_COLLECTION, dateKey);
      const snap = await getDoc(docRef);
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() } as LinkedInDailyMetrics;
    } catch (error) {
      console.error(`Error fetching metrics for ${dateKey}:`, error);
      return null;
    }
  }

  /**
   * Get today's metrics.
   */
  static async getTodayMetrics(): Promise<LinkedInDailyMetrics | null> {
    return this.getMetrics(todayDateKey());
  }

  /**
   * Save or update daily metrics. Uses setDoc with merge to upsert.
   */
  static async saveMetrics(
    dateKey: string,
    data: Partial<Omit<LinkedInDailyMetrics, 'id' | 'date' | 'timestamp'>>
  ): Promise<void> {
    const docRef = doc(db, METRICS_COLLECTION, dateKey);
    await setDoc(docRef, {
      date: dateKey,
      ...data,
      timestamp: Timestamp.now(),
      updatedAt: Timestamp.now(),
    }, { merge: true });
  }

  /**
   * Get recent metrics (last N days).
   */
  static async getRecentMetrics(days: number = 30): Promise<LinkedInDailyMetrics[]> {
    try {
      const q = query(
        getMetricsCollection(),
        orderBy('date', 'desc'),
        limit(days)
      );
      const snapshot = await getDocs(q);

      return snapshot.docs
        .map(d => ({ id: d.id, ...d.data() } as LinkedInDailyMetrics))
        .reverse(); // Chronological order for charts
    } catch (error) {
      console.error('Error fetching recent metrics:', error);
      return [];
    }
  }

  /**
   * Aggregate metrics over a date range.
   */
  static aggregateMetrics(metrics: LinkedInDailyMetrics[]): {
    totalImpressions: number;
    totalReactions: number;
    totalComments: number;
    totalReposts: number;
    avgProfileViews: number;
    totalConnectionsSent: number;
    totalConnectionsAccepted: number;
    totalDmsSent: number;
    totalDmsReceived: number;
    totalCommentsLeft: number;
    connectionAcceptanceRate: number;
  } {
    const totals = {
      totalImpressions: 0,
      totalReactions: 0,
      totalComments: 0,
      totalReposts: 0,
      avgProfileViews: 0,
      totalConnectionsSent: 0,
      totalConnectionsAccepted: 0,
      totalDmsSent: 0,
      totalDmsReceived: 0,
      totalCommentsLeft: 0,
      connectionAcceptanceRate: 0,
    };

    let profileViewDays = 0;
    let totalProfileViews = 0;

    metrics.forEach(m => {
      totals.totalImpressions += m.impressions || 0;
      totals.totalReactions += m.reactions || 0;
      totals.totalComments += m.comments || 0;
      totals.totalReposts += m.reposts || 0;
      totals.totalConnectionsSent += m.connectionsSent || 0;
      totals.totalConnectionsAccepted += m.connectionsAccepted || 0;
      totals.totalDmsSent += m.dmsSent || 0;
      totals.totalDmsReceived += m.dmsReceived || 0;
      totals.totalCommentsLeft += m.commentsLeft || 0;

      if (m.profileViews !== undefined) {
        totalProfileViews += m.profileViews;
        profileViewDays++;
      }
    });

    if (profileViewDays > 0) {
      totals.avgProfileViews = Math.round(totalProfileViews / profileViewDays);
    }

    if (totals.totalConnectionsSent > 0) {
      totals.connectionAcceptanceRate = Math.round(
        (totals.totalConnectionsAccepted / totals.totalConnectionsSent) * 100
      );
    }

    return totals;
  }
}
